import { motion } from "framer-motion";
import { BadgeDollarSign, MapPin, Medal } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const POINTS = [
  { icon: BadgeDollarSign, title: "Affordable", text: "Fair starting prices." },
  { icon: MapPin, title: "Local", text: "Located in Oak Grove & Grain Valley." },
  { icon: Medal, title: "Hard-working", text: "We take pride in getting the job done." },
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="py-24 bg-pine relative overflow-hidden">
      <div
        className="pointer-events-none absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-limed/10 blur-3xl"
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, x: -32 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="relative"
        >
          <div className="overflow-hidden rounded-[2.5rem] rounded-bl-[6rem] shadow-2xl shadow-black/30">
            <img
              src="https://images.unsplash.com/photo-1758613171187-9a41d0fc93ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHx5b3VuZyUyMGFkdWx0cyUyMHRlYW0lMjBoYXJkJTIwd29ya2luZyUyMG91dGRvb3J8ZW58MHx8fHwxNzkwNjI4NzU4fDA&ixlib=rb-4.1.0&q=85"
              alt="The All-Season Crew team working outdoors"
              loading="lazy"
              className="w-full h-[380px] lg:h-[460px] object-cover hover:scale-105 transition-transform duration-700 ease-out"
            />
          </div>
          <div className="absolute -top-5 -right-4 bg-limed text-pine-dark rounded-2xl shadow-xl px-5 py-3 animate-float">
            <p className="font-display font-bold text-lg leading-none">100% local</p>
            <p className="text-[11px] font-mono uppercase tracking-widest mt-1 opacity-70">teen-run crew</p>
          </div>
        </motion.div>

        <motion.div
          data-testid="crew-story-card"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
        >
          <p className="text-xs uppercase tracking-[0.25em] font-mono font-semibold text-limed-bright mb-4">
            Why All-Season Crew?
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight text-bone leading-[1.08]">
            A team of teens building something real.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-bone/70 leading-relaxed">
            We are a team of teens building a local business and working hard for every customer.
            When you hire us, you're supporting your neighbors — and we show up like it.
          </p>

          <div className="mt-9 space-y-4">
            {POINTS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 + i * 0.12, ease: EASE }}
                className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-2xl px-5 py-4 hover:bg-white/10 hover:border-limed/40 transition-colors duration-300"
              >
                <div className="w-11 h-11 shrink-0 rounded-xl bg-limed flex items-center justify-center text-pine-dark">
                  <p.icon size={20} strokeWidth={2.2} />
                </div>
                <div>
                  <p className="font-display font-bold text-bone">{p.title}</p>
                  <p className="text-sm text-bone/60">{p.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
